import { useMemo } from 'react';
import LoadingOverlay from 'components/common/LoadingOverlay';
import LifestyleCalculatorForm from 'components/budget/form';
import useMyBudget from 'hooks/budget/useMyBudget';
import { isNull, round } from 'lodash';
import { BudgetCalculatorImportance } from 'graphql/generated/graphql';
import type {
  ILifestyleFormValues,
  TMonthlyExpenseKey,
  TMyBudget,
} from 'components/budget/form/types';
import { toNumberValueKey, userInputOrCalculatedValue } from 'components/budget/form/utils';

export const lifestyleCalculatorInitialValues: ILifestyleFormValues = {
  own_funds: null,
  project_type: null,
  buying_with_agent: null,
  square_meters: null,
  alone_or_partner: null,
  monthly_net_salary: null,
  socializing: BudgetCalculatorImportance.not_important,
  luxuries: BudgetCalculatorImportance.not_important,
  taking_a_break: BudgetCalculatorImportance.not_important,
  hobbies: BudgetCalculatorImportance.not_important,
  technology: BudgetCalculatorImportance.not_important,
  pension_and_insurances: BudgetCalculatorImportance.not_important,
  new_car: BudgetCalculatorImportance.not_important,
  cars: null,
  kids_quantity: null,
  day: null,
  month: null,
  year: null,
  monthly_net_salary_partner: null,
  other_revenues: null,
  amount_of_alimony: null,
  monthly_loan: null,
  broker_commission: null,
  property_transfer_tax: null,
  land_register_fee: null,
  notary_fee: null,
};

const monthlyExpensesKeys: TMonthlyExpenseKey[] = [
  'socializing',
  'luxuries',
  'taking_a_break',
  'hobbies',
  'technology',
  'pension_and_insurances',
  'new_car',
];

// Only the amounts: the calculation values the former code kept under the category keys were
// replaced by `formData` (spread after them, always there with a budget).
function getMonthlyExpensesInitialValues(lifestyleCalculations?: TMyBudget | null) {
  if (!lifestyleCalculations) return {};
  const allowedValues: ILifestyleFormValues = {};

  monthlyExpensesKeys.forEach((key) => {
    const item = lifestyleCalculations.calculation[key];
    if (item.calculated_value === 0 && !Number.isFinite(item.user_input)) return;
    if (Number.isFinite(item.calculated_value)) {
      const value = userInputOrCalculatedValue(item);
      allowedValues[toNumberValueKey(key)] = round(value);
    }
  });
  return allowedValues;
}

export function getInitialLifestyleCalculatorValues(myBudget?: TMyBudget | null) {
  return {
    ...lifestyleCalculatorInitialValues,
    ...getMonthlyExpensesInitialValues(myBudget),
    ...myBudget?.formData,
  };
}

const LifestyleCalculator = () => {
  const [myBudget, loading] = useMyBudget();
  const initialValues = useMemo(() => getInitialLifestyleCalculatorValues(myBudget), [myBudget]);

  const calculatorCompleted = useMemo(
    () => (!myBudget ? false : isNull(myBudget.step)),
    [myBudget],
  );
  const initialStep = useMemo(
    () => (!myBudget || isNull(myBudget.step) ? 1 : myBudget.step),
    [myBudget],
  );

  if (loading) return <LoadingOverlay />;

  return (
    <LifestyleCalculatorForm {...{ initialValues, myBudget, calculatorCompleted, initialStep }} />
  );
};

export default LifestyleCalculator;
